import React from 'react';
import PropTypes from 'prop-types';
import { Box, Skeleton, TableCell, TableRow } from '@mui/material';
import { C } from './tokens';

// Placeholder shown above the table while initial summary data is loading.
export const BreakoutSummarySkeleton = ({ isCompact }) => {
  if (isCompact) {
    return (
      <Box sx={{ py: 1.1, borderBottom: `1px solid ${C.borderSoft}` }}>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 0.9 }}>
          {['s1', 's2', 's3', 's4'].map((slotKey) => (
            <Box
              key={`summary-skeleton-${slotKey}`}
              sx={{ p: 1.15, borderRadius: 1.5, border: `1px solid ${C.border}`, bgcolor: '#fff' }}
            >
              <Skeleton variant="text" width="42%" height={14} />
              <Skeleton variant="text" width="56%" height={26} sx={{ mt: 0.35 }} />
              <Skeleton variant="text" width="74%" height={16} sx={{ mt: 0.25 }} />
            </Box>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2,
        py: 1,
        borderBottom: `1px solid ${C.borderSoft}`,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, flexWrap: 'wrap', minWidth: 0 }}>
        <Skeleton variant="text" width={110} height={22} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Skeleton variant="rounded" width={130} height={8} />
          <Skeleton variant="text" width={120} height={22} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          {['d1', 'd2', 'd3'].map((dotKey) => (
            <Skeleton key={`regime-dot-${dotKey}`} variant="circular" width={10} height={10} />
          ))}
        </Box>
        <Skeleton variant="text" width={120} height={22} />
        <Skeleton variant="text" width={150} height={22} />
      </Box>
      <Skeleton variant="text" width={160} height={20} />
    </Box>
  );
};

BreakoutSummarySkeleton.propTypes = {
  isCompact: PropTypes.bool,
};

BreakoutSummarySkeleton.defaultProps = {
  isCompact: false,
};

const BreakoutTableSkeletonRow = ({ isCompact, rowKey }) => (
  <TableRow key={rowKey}>
    <TableCell align="center" sx={{ py: 1.2 }}>
      <Skeleton variant="circular" width={22} height={22} sx={{ mx: 'auto' }} />
    </TableCell>
    <TableCell sx={{ py: 1.2 }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.45 }}>
        <Skeleton variant="text" width={44} height={20} />
        <Skeleton variant="text" width={isCompact ? '88%' : '76%'} height={16} />
        {isCompact ? <Skeleton variant="text" width="48%" height={16} /> : null}
      </Box>
    </TableCell>
    {isCompact ? null : <TableCell sx={{ py: 1.2 }}><Skeleton variant="text" width="78%" height={18} /></TableCell>}
    <TableCell align="right" sx={{ py: 1.2 }}><Skeleton variant="text" width={42} height={18} sx={{ ml: 'auto' }} /></TableCell>
    <TableCell sx={{ py: 1.2 }}><Skeleton variant="rounded" width={92} height={28} /></TableCell>
    <TableCell sx={{ py: 1.2 }}><Skeleton variant="rounded" width={56} height={20} /></TableCell>
    <TableCell align="right" sx={{ py: 1.2 }}><Skeleton variant="text" width={62} height={18} sx={{ ml: 'auto' }} /></TableCell>
    {isCompact ? null : (
      <>
        <TableCell align="right" sx={{ py: 1.2 }}><Skeleton variant="text" width={58} height={18} sx={{ ml: 'auto' }} /></TableCell>
        <TableCell align="right" sx={{ py: 1.2 }}><Skeleton variant="text" width={48} height={18} sx={{ ml: 'auto' }} /></TableCell>
        <TableCell align="right" sx={{ py: 1.2 }}><Skeleton variant="text" width={40} height={18} sx={{ ml: 'auto' }} /></TableCell>
        <TableCell align="right" sx={{ py: 1.2 }}><Skeleton variant="text" width={56} height={18} sx={{ ml: 'auto' }} /></TableCell>
        <TableCell align="right" sx={{ py: 1.2 }}><Skeleton variant="text" width={44} height={18} sx={{ ml: 'auto' }} /></TableCell>
        <TableCell align="right" sx={{ py: 1.2 }}><Skeleton variant="text" width={34} height={18} sx={{ ml: 'auto' }} /></TableCell>
        <TableCell sx={{ py: 1.2 }}><Skeleton variant="rounded" width={72} height={24} /></TableCell>
      </>
    )}
  </TableRow>
);

BreakoutTableSkeletonRow.propTypes = {
  isCompact: PropTypes.bool,
  rowKey: PropTypes.string.isRequired,
};

BreakoutTableSkeletonRow.defaultProps = {
  isCompact: false,
};

// Sequence of skeleton rows rendered inside the table body when initially loading.
export const BreakoutTableSkeletonRows = ({ isCompact, rowCount }) => (
  <>
    {Array.from({ length: rowCount }, (_, index) => `breakout-skeleton-row-${index}`).map((rowKey) => (
      <BreakoutTableSkeletonRow
        key={rowKey}
        rowKey={rowKey}
        isCompact={isCompact}
      />
    ))}
  </>
);

BreakoutTableSkeletonRows.propTypes = {
  isCompact: PropTypes.bool,
  rowCount: PropTypes.number,
};

BreakoutTableSkeletonRows.defaultProps = {
  isCompact: false,
  rowCount: 6,
};

// Skeleton for the filter bar while metadata fetch is in flight.
export const BreakoutFilterSkeleton = () => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: {
        xs: 'repeat(2, minmax(0, 1fr))',
        sm: 'repeat(3, minmax(0, 1fr))',
        md: 'repeat(4, minmax(0, 1fr))',
        lg: 'minmax(220px, 1.45fr) repeat(6, minmax(110px, 1fr)) auto',
      },
      gap: 1,
      py: 0.5,
      borderBottom: `1px solid ${C.borderSoft}`,
      alignItems: 'stretch',
    }}
  >
    {['32%', '36%', '30%', '34%', '28%', '24%', '30%'].map((labelWidth, index) => (
      <Box
        key={`filter-skeleton-${labelWidth}-${index}`}
        sx={{
          gridColumn: index === 0 ? { xs: '1 / -1', lg: 'auto' } : undefined,
          position: 'relative',
        }}
      >
        <Skeleton
          variant="rounded"
          width={labelWidth}
          height={14}
          sx={{ position: 'absolute', top: -1, left: 12, zIndex: 1, borderRadius: 0.8 }}
        />
        <Skeleton
          variant="rounded"
          width="100%"
          height={44}
          sx={{ mt: 0.8, borderRadius: 1.2 }}
        />
      </Box>
    ))}
    <Skeleton
      variant="rounded"
      width="100%"
      height={36}
      sx={{
        alignSelf: 'end',
        borderRadius: 1.2,
        gridColumn: { xs: '1 / -1', sm: 'span 1', lg: 'auto' },
      }}
    />
  </Box>
);
